import { Button } from '@/components/ui/button';
import type { IntakeAnswerValue, IntakeQuestion } from '@/api/portalIntake';

export const INTAKE_INPUT_CLASS =
  'w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-brand-navy placeholder:text-brand-slate focus:border-brand-royal focus:outline-none';

/** One answer input, switched on the question's answer type. Shared by
 *  the all-questions card (PortalIntakeCard) and the one-question-per-
 *  card onboarding wizard. The boolean / multi-select variants are
 *  toggle-button groups (`aria-pressed` carries the selected state)
 *  labelled via `${inputId}-label` — the caller renders that label
 *  element. */
export function IntakeInput({
  inputId,
  question,
  value,
  onChange,
  autoFocus,
}: {
  inputId: string;
  question: IntakeQuestion;
  value: IntakeAnswerValue | undefined;
  onChange: (value: IntakeAnswerValue) => void;
  autoFocus?: boolean;
}) {
  switch (question.answer_type) {
    case 'boolean':
      return (
        <div className="flex gap-2" role="group" aria-labelledby={`${inputId}-label`}>
          {[true, false].map((option) => (
            <Button
              key={String(option)}
              type="button"
              size="sm"
              variant={value === option ? 'default' : 'outline'}
              aria-pressed={value === option}
              onClick={() => onChange(option)}
            >
              {option ? 'Yes' : 'No'}
            </Button>
          ))}
        </div>
      );
    case 'single_select':
      return (
        <select
          id={inputId}
          className={INTAKE_INPUT_CLASS}
          value={typeof value === 'string' ? value : ''}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        >
          <option value="">Select…</option>
          {question.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    case 'multi_select':
      return (
        <div className="flex flex-wrap gap-2" role="group" aria-labelledby={`${inputId}-label`}>
          {question.options.map((option) => {
            const selected = Array.isArray(value) && value.includes(option.value);
            return (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={selected ? 'default' : 'outline'}
                aria-pressed={selected}
                onClick={() => {
                  const current = Array.isArray(value) ? value : [];
                  onChange(
                    selected
                      ? current.filter((v) => v !== option.value)
                      : [...current, option.value],
                  );
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      );
    case 'date':
      return (
        <input
          id={inputId}
          type="date"
          className={INTAKE_INPUT_CLASS}
          value={typeof value === 'string' ? value : ''}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      );
    default:
      return (
        <input
          id={inputId}
          type="text"
          className={INTAKE_INPUT_CLASS}
          value={typeof value === 'string' ? value : ''}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      );
  }
}
